/**
 * OneTimeOptionEditor — Editor da opção AVULSA (compra única) de um add-on
 *
 * Substitui o RecurrenceOptionsEditor quando o add-on está em
 * `billing_mode='avulso'`. Apresenta um único bloco com:
 *   - Select de serviço avulso do tenant master (wl_list_master_one_time_services)
 *   - Preço por unidade, Setup, Desconto %
 *   - Switch ativo/inativo (somente quando já persistido)
 *
 * Variantes:
 *   - <OneTimeOptionEditor entityId={addonId} />: modo edição persistido.
 *   - <OneTimeOptionInlineEditor value onChange />: modo criação in-memory
 *     (usado no diálogo de criar add-on, antes do id existir).
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import {
  useWlAddonOneTimeOption,
  useWlAddonOneTimeOptionMutations,
  useWlMasterOneTimeServices,
} from "@/hooks/wl/useWlAddonOneTimeOption";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { showToast } from "@/lib/toast";
import { applyCurrencyMask, parseCurrency } from "@/lib/input-masks";

// ===========================================================================
// Modo EDIÇÃO persistido
// ===========================================================================
interface PersistedProps {
  entityId: string;
}

export function OneTimeOptionEditor({ entityId }: PersistedProps) {
  const q = useWlAddonOneTimeOption(entityId);
  const m = useWlAddonOneTimeOptionMutations(entityId);

  const current = q.data || null;

  if (q.isLoading) {
    return (
      <div className="rounded-lg border p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
      </div>
    );
  }

  return (
    <OneTimeOptionForm
      current={current}
      onSave={async (payload) => {
        try {
          await m.upsert.mutateAsync({
            id: current?.id ?? null,
            payload: { ...payload, addon_id: entityId },
          });
          showToast.success("Opção avulsa salva");
        } catch (e: any) {
          showToast.error(e?.message || "Falha ao salvar opção avulsa");
        }
      }}
      onToggleActive={async (active) => {
        if (!current) return;
        await m.toggleActive.mutateAsync({ id: current.id, active });
      }}
      onRemove={async () => {
        if (!current) return;
        if (!confirm("Remover a opção avulsa deste add-on?")) return;
        try {
          await m.remove.mutateAsync(current.id);
          showToast.success("Opção avulsa removida");
        } catch (e: any) {
          showToast.error(e?.message || "Falha ao remover");
        }
      }}
    />
  );
}

// ===========================================================================
// Modo CRIAÇÃO (in-memory) — usado antes do add-on existir no banco
// ===========================================================================
export interface PendingOneTimeOption {
  uaizy_master_service_id: string;
  price: number;
  setup_fee: number;
  discount_percent: number | null;
  is_active: boolean;
}

interface InlineProps {
  value: PendingOneTimeOption | null;
  onChange: (next: PendingOneTimeOption | null) => void;
}

export function OneTimeOptionInlineEditor({ value, onChange }: InlineProps) {
  // Adapta o "current" para o mesmo formato do persistido.
  const adapted = useMemo(() => {
    if (!value) return null;
    return {
      id: "__pending__",
      addon_id: "",
      uaizy_master_service_id: value.uaizy_master_service_id,
      service_name: null,
      service_unit_type: null,
      service_price: 0,
      price: value.price,
      setup_fee: value.setup_fee,
      discount_percent: value.discount_percent,
      stripe_price_id: null,
      is_active: value.is_active,
      sort_order: 0,
    };
  }, [value]);

  return (
    <OneTimeOptionForm
      current={adapted}
      // No modo inline o botão "Salvar" apenas atualiza o estado controlado.
      onSave={async (payload) => {
        onChange({
          uaizy_master_service_id: payload.uaizy_master_service_id as string,
          price: payload.price as number,
          setup_fee: payload.setup_fee as number,
          discount_percent: payload.discount_percent as number | null,
          is_active: (payload.is_active as boolean) ?? true,
        });
        showToast.success("Opção avulsa pronta — clique em Criar para gravar");
      }}
      onToggleActive={async (active) => {
        if (!value) return;
        onChange({ ...value, is_active: active });
      }}
      onRemove={async () => {
        onChange(null);
      }}
      inline
    />
  );
}

// ===========================================================================
// Formulário visual compartilhado
// ===========================================================================
interface FormProps {
  current: {
    id: string;
    uaizy_master_service_id: string;
    price: number;
    setup_fee: number;
    discount_percent: number | null;
    is_active: boolean;
  } | null;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onToggleActive: (active: boolean) => Promise<void>;
  onRemove: () => Promise<void>;
  inline?: boolean;
}

function OneTimeOptionForm({
  current,
  onSave,
  onToggleActive,
  onRemove,
  inline,
}: FormProps) {
  const services = useWlMasterOneTimeServices(null);
  const [serviceId, setServiceId] = useState<string>(
    current?.uaizy_master_service_id || "",
  );
  const [price, setPrice] = useState<number>(current?.price ?? 0);
  const [setup, setSetup] = useState<number>(current?.setup_fee ?? 0);
  const [discount, setDiscount] = useState<string>(
    current?.discount_percent !== null && current?.discount_percent !== undefined
      ? String(current.discount_percent)
      : "",
  );

  // Re-sincroniza ao trocar de add-on/registro.
  useEffect(() => {
    setServiceId(current?.uaizy_master_service_id || "");
    setPrice(current?.price ?? 0);
    setSetup(current?.setup_fee ?? 0);
    setDiscount(
      current?.discount_percent !== null && current?.discount_percent !== undefined
        ? String(current.discount_percent)
        : "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  const dirty =
    !current ||
    serviceId !== current.uaizy_master_service_id ||
    price !== current.price ||
    setup !== current.setup_fee ||
    (discount === ""
      ? current.discount_percent !== null
      : Number(discount) !== current.discount_percent);

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Label className="text-sm font-semibold">Cobrança avulsa</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Add-on de compra única (sem periodicidade). Vincule um serviço
            avulso do tenant master e defina o preço por unidade.
          </p>
        </div>
      </div>

      <div className="rounded-md border p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {current ? (
              <Switch
                checked={current.is_active}
                onCheckedChange={(v) => onToggleActive(v)}
              />
            ) : (
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-dashed text-muted-foreground">
                Não configurado
              </span>
            )}
            <span className="text-sm font-medium">Compra única</span>
          </div>
          <div className="flex items-center gap-2">
            {current && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={onRemove}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              size="sm"
              disabled={!serviceId || !dirty}
              onClick={() =>
                onSave({
                  uaizy_master_service_id: serviceId,
                  price,
                  setup_fee: setup,
                  discount_percent: discount === "" ? null : Number(discount),
                  is_active: current?.is_active ?? true,
                })
              }
            >
              <Save className="h-4 w-4 mr-1" />
              {inline ? "Aplicar" : "Salvar"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-6 space-y-1">
            <Label className="text-xs">Serviço avulso do master</Label>
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    services.isLoading
                      ? "Carregando…"
                      : services.data && services.data.length > 0
                        ? "Selecionar serviço"
                        : "Nenhum serviço avulso no master"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(services.data || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.price ? ` — R$ ${Number(s.price).toFixed(2)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-1">
            <Label className="text-xs">Preço por unidade</Label>
            <Input
              placeholder="R$ 0,00"
              value={price > 0 ? applyCurrencyMask(price) : ""}
              onChange={(e) => setPrice(parseCurrency(e.target.value))}
            />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs">Setup</Label>
            <Input
              placeholder="R$ 0,00"
              value={setup > 0 ? applyCurrencyMask(setup) : ""}
              onChange={(e) => setSetup(parseCurrency(e.target.value))}
            />
          </div>
          <div className="md:col-span-1 space-y-1">
            <Label className="text-xs">Desc %</Label>
            <Input
              type="number"
              min={0}
              max={100}
              placeholder="0"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
